import { useMemo, useCallback } from "react";
import { useSelector } from "react-redux";

export default function useCheckPermission() {
  const permissions = useSelector((state) => state?.auth?.permissions || {});

  const permissionMap = useMemo(() => {
    const map = {};

    for (const module in permissions) {
      map[module] = new Set(permissions[module] || []);
    }

    return map;
  }, [permissions]);

  const hasModule = useCallback(
    (module) => !!permissionMap[module],
    [permissionMap]
  );

  const can = useCallback(
    (module, action) =>
      !!module && !!action && permissionMap[module]?.has(action),
    [permissionMap]
  );

  const canAny = useCallback(
    (module, actions = []) =>
      !!permissionMap[module] &&
      actions.some((a) => permissionMap[module].has(a)),
    [permissionMap]
  );

  const canAll = useCallback(
    (module, actions = []) =>
      !!permissionMap[module] &&
      actions.every((a) => permissionMap[module].has(a)),
    [permissionMap]
  );

  const hasAnyModule = useCallback(
    (modules = []) => modules.some((m) => permissionMap[m]),
    [permissionMap]
  );

  const hasAllModules = useCallback(
    (modules = []) => modules.every((m) => permissionMap[m]),
    [permissionMap]
  );

  const canMany = useCallback(
    (list = [], mode = "all") => {
      const checks = list.map((key) => {
        const [module, action] = key.split(".");
        return permissionMap[module]?.has(action);
      });

      return mode === "any"
        ? checks.some(Boolean)
        : checks.every(Boolean);
    },
    [permissionMap]
  );

  return {
    can,
    canAny,
    canAll,
    hasModule,
    hasAnyModule,
    hasAllModules,
    canMany
  };
}
